import cloneDeep from 'lodash/cloneDeep'
import set from 'lodash/fp/set'
import { AnyAction, CombinedState, combineReducers, Reducer } from 'redux'
import { type PersistMigrate } from 'redux-persist'

import { PageId } from 'routing/pages'

import adGroupPage from './adGroupPage'
import adGroupsSummary from './adGroupsSummary'
import app from './app'
import authOrganizationPage from './authOrganizationPage'
import automationPage from './automationPage'
import automationProfilePage from './automationProfilePage'
import brandPage from './brandPage'
import brandsSummary from './brandsSummary'
import campaignPage from './campaignPage'
import campaignsSummary from './campaignsSummary'
import dashboardPage from './dashboardPage'
import dashboardPrintPage from './dashboardPrintPage'
import dashboardsSummary from './dashboardsSummary'
import dspAdvertiserPage from './dspAdvertiserPage'
import dspLineItemPage from './dspLineItemPage'
import dspOrderPage from './dspOrderPage'
import dspSummary from './dspSummary'
import homePage from './homePage'
import keywordPage from './keywordPage'
import portfolioPage from './portfolioPage'
import portfoliosSummary from './portfoliosSummary'
import productAdPage from './productAdPage'
import productPage from './productPage'
import productsSummary from './productsSummary'
import reportsSummary from './reportsSummary'
import rulebookPage from './rulebookPage'
import targetingSummary from './targetingSummary'

export type StaticReducerKey = Extract<
    PageId,
    | 'app'
    | 'adAccountsSummaryPage'
    | 'adAccountPage'
    | 'adGroupPage'
    | 'adGroupsSummaryPage'
    | 'authOrganizationPage'
    | 'automationPage'
    | 'automationProfilePage'
    | 'campaignsSummaryPage'
    | 'campaignPage'
    | 'dashboardsSummaryPage'
    | 'dashboardPage'
    | 'dashboardPrintPage'
    | 'dspAdvertiserPage'
    | 'dspLineItemPage'
    | 'dspOrderPage'
    | 'dspSummaryPage'
    | 'homePage'
    | 'keywordPage'
    | 'portfolioPage'
    | 'portfoliosSummaryPage'
    | 'productAdPage'
    | 'productPage'
    | 'productsSummaryPage'
    | 'reportsSummaryPage'
    | 'rulebookPage'
    | 'targetingSummaryPage'
>

export type DynamicReducerKey = Extract<
    PageId,
    | 'alertCreationPage'
    | 'alertPage'
    | 'categoryPerformanceSummaryPage'
    | 'categoryDetailsPage'
    | 'distributionAnalysisPage'
    | 'executiveHomePage'
    | 'keywordResearchPage'
    | 'keywordResearchListsPage'
    | 'keywordResearchListDetailPage'
    | 'labelPage'
    | 'organizationGroupPage'
    | 'productCatalogPage'
    | 'productDetailPage'
    | 'segmentCreationPage'
    | 'segmentEditPage'
    | 'segmentPage'
    | 'segmentsSummaryPage'
    | 'settingsPage'
    | 'sovKeywordPage'
    | 'sovKeywordSearchResultPage'
    | 'sovKeywordsSummaryPage'
>

// This function is a util to validate
// that all values in DynamicReducerKey type are in an array.
// This lets us use a string literal type to get better typing on all values
// and also make sure that our dynamicReducerKeys array is always up to date.
const allDynamicReducerKeys = <T extends DynamicReducerKey[]>(
    array: T & ([DynamicReducerKey] extends [T[number]] ? unknown : 'Invalid')
): T & ([DynamicReducerKey] extends [T[number]] ? unknown : 'Invalid') => array

const dynamicReducerKeys: DynamicReducerKey[] = allDynamicReducerKeys([
    'alertCreationPage',
    'alertPage',
    'categoryPerformanceSummaryPage',
    'categoryDetailsPage',
    'distributionAnalysisPage',
    'executiveHomePage',
    'keywordResearchPage',
    'keywordResearchListsPage',
    'keywordResearchListDetailPage',
    'labelPage',
    'organizationGroupPage',
    'productCatalogPage',
    'productDetailPage',
    'segmentCreationPage',
    'segmentEditPage',
    'segmentPage',
    'segmentsSummaryPage',
    'settingsPage',
    'sovKeywordPage',
    'sovKeywordSearchResultPage',
    'sovKeywordsSummaryPage',
])

export interface DynamicReducerOptions {
    migrations?: PersistMigrate
    version?: number
}

// TODO: Improve typing here - the reducers here are typed differently
// Ideally we should return Reducer<any, AnyAction>
const staticReducers: Record<StaticReducerKey, Reducer<any, AnyAction>> = {
    app,
    adAccountsSummaryPage: brandsSummary,
    adAccountPage: brandPage,
    adGroupPage: adGroupPage,
    adGroupsSummaryPage: adGroupsSummary,
    authOrganizationPage: authOrganizationPage,
    automationPage: automationPage,
    automationProfilePage: automationProfilePage,
    campaignsSummaryPage: campaignsSummary,
    campaignPage: campaignPage,
    dashboardsSummaryPage: dashboardsSummary,
    dashboardPage: dashboardPage,
    dashboardPrintPage: dashboardPrintPage,
    dspAdvertiserPage: dspAdvertiserPage,
    dspLineItemPage: dspLineItemPage,
    dspOrderPage: dspOrderPage,
    dspSummaryPage: dspSummary,
    homePage: homePage,
    keywordPage: keywordPage,
    portfoliosSummaryPage: portfoliosSummary,
    portfolioPage: portfolioPage,
    productsSummaryPage: productsSummary,
    productAdPage: productAdPage,
    productPage: productPage,
    reportsSummaryPage: reportsSummary,
    rulebookPage: rulebookPage,
    targetingSummaryPage: targetingSummary,
}

const asyncReducers = {} as Record<DynamicReducerKey, Reducer<any, AnyAction>>

export const getAllAsyncReducerKeys = (): readonly DynamicReducerKey[] => {
    return dynamicReducerKeys
}

export const injectUiReducer = (
    key: DynamicReducerKey,
    asyncReducer: Reducer<any, AnyAction>
): void => {
    if (!getAllAsyncReducerKeys().includes(key)) {
        throw new Error(
            `Unexpected reducer key. ${key} is not in the list of dynamically loaded reducers.`
        )
    }
    asyncReducers[key] = asyncReducer
}

export const getDefaultState = (): CombinedState<
    Record<StaticReducerKey | DynamicReducerKey, Reducer<any, AnyAction>>
> => {
    const combinedReducers = combineReducers({
        ...staticReducers,
        ...asyncReducers,
    })
    return combinedReducers(undefined, {} as any)
}

// Save a copy of the initial default state for resets, the default state may be updated by redux-persist
let pristineUiStateClone = cloneDeep(getDefaultState())

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getPristineUiStateClone = () => pristineUiStateClone

export const updatePristineUiStateClone = (
    state: any,
    path: string[]
): void => {
    if (state) {
        pristineUiStateClone = set(path, state, pristineUiStateClone)
    }
}

export const buildUiReducer = (): Reducer<any, AnyAction> => {
    return combineReducers({ ...staticReducers, ...asyncReducers })
}
